/**
 * New exploration: every choice on one page.
 *
 * One card per decision (tag, optimization, laser system, design space,
 * substrate, substrate position, camera calibration, width measurement, then
 * the read-only configurations, prepass and initial population), stacked vertically, with
 * a sticky review panel beside them (the composed ID, the setup checklist,
 * the Start button). A collapsed card shows its current value in one line.
 * Every setting the run reads from its configuration documents is a field
 * on the card it belongs to (setup/useExplorationConfigs.js owns the
 * documents); an invalid field blocks Start and is named in the review
 * panel. The cards are pre-filled from the most recent
 * exploration (the user's own, else the team's; helper/explorationSetupDefaults.js),
 * so only what differs has to be changed: the tag card starts open because
 * the tag is always new, and so does every card whose value could not be
 * pre-filled. The exploration is created by Start
 * (setup/ExplorationStartSection.js). The draft lives in this page's state,
 * so a reload starts over.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, Stack } from "@mui/material";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import axios from "axios";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import GroupLabel from "../../components/GroupLabel";
import { EMPTY_EXPLORATION_DRAFT } from "./setup/explorationDraft";
import SetupCard from "./setup/SetupCard";
import NameCard from "./setup/NameCard";
import OptimizationCard, { enabledObjectives, enabledRules } from "./setup/OptimizationCard";
import LaserCard, { laserSummary } from "./setup/LaserCard";
import DesignSpaceCard, { designSpaceSummary } from "./setup/DesignSpaceCard";
import SubstrateCard, { plateDesignSummary } from "./setup/SubstrateCard";
import WidthMeasurementCard, { widthSummary } from "./setup/WidthMeasurementCard";
import PlatePositionCard from "./setup/PlatePositionCard";
import CalibrationCard, { calibrationSummary } from "./setup/CalibrationCard";
import useExplorationConfigs from "./setup/useExplorationConfigs";
import ReviewPanel from "./setup/ReviewPanel";
import ExplorationStartSection from "./setup/ExplorationStartSection";
import {
  EXPLORATION_TAG_PATTERN,
  composeExplorationName,
  previewExplorationName,
} from "../../helper/explorationName";
import { defaultsFromExploration, resolveChoice } from "../../helper/explorationSetupDefaults";
import { optimizerLabel, optimizerOf } from "../../helper/optimizerChoice";

export const LATEST_MINE_URL = "api/exploration/data?scope=mine&page=0&per_page=1";
export const LATEST_TEAM_URL = "api/exploration/data?page=0&per_page=1";

async function fetchList(url, mapRow) {
  try {
    const res = await axios.get(url);
    return Array.isArray(res.data) ? res.data.map(mapRow) : [];
  } catch (err) {
    console.error(err);
    return [];
  }
}

/** The most recent exploration to take the defaults from: the user's own,
 *  else the team's, else none. */
async function fetchLatestExploration() {
  const sources = [
    [LATEST_MINE_URL, "mine"],
    [LATEST_TEAM_URL, "team"],
  ];
  for (const [url, scope] of sources) {
    try {
      const res = await axios.get(url);
      const row = res.data?.data?.[0];
      if (row) return { row, scope };
    } catch (err) {
      console.error(err);
    }
  }
  return { row: null, scope: null };
}

export default function ColorExplorationSetup() {
  const [draft, setDraft] = useState(EMPTY_EXPLORATION_DRAFT);
  const updateDraft = useCallback((partial) => {
    setDraft((current) => ({ ...current, ...partial }));
  }, []);
  const [lists, setLists] = useState({ objectives: [], lasers: [], materials: [], calibrations: [] });
  // Whose most recent exploration the defaults came from ("mine" | "team"),
  // or null when there was none. The exploration itself is not named.
  const [defaultsScope, setDefaultsScope] = useState(null);
  const [ready, setReady] = useState(false);
  const [openCards, setOpenCards] = useState(() => new Set(["name"]));
  // The server's plate layout for the current laser, substrate and offset;
  // null while the plate lies outside the marking field.
  const [layout, setLayout] = useState(null);
  // The review panel's box the start section renders its actions into.
  const [actionsNode, setActionsNode] = useState(null);
  // The configuration documents the cards edit and Start stores.
  const configs = useExplorationConfigs(draft);
  // How many fields of each card hold an invalid value (blocks Start).
  const [invalid, setInvalid] = useState({});
  const invalidSetters = useMemo(() => {
    const setter = (key) => (count) =>
      setInvalid((current) => (current[key] === count ? current : { ...current, [key]: count }));
    return {
      optimization: setter("optimization"),
      laser: setter("laser"),
      design: setter("design"),
      substrate: setter("substrate"),
      position: setter("position"),
      width: setter("width"),
    };
  }, []);
  // The chosen substrate's row (plate size), shown on the Substrate card.
  const [substrate, setSubstrate] = useState(null);
  useEffect(() => {
    const materialId = draft.materialId;
    if (!materialId) {
      setSubstrate(null);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`api/color/explorer/material/${materialId}/`)
      .then((res) => {
        if (!cancelled && res.status === 200) setSubstrate(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
    return () => {
      cancelled = true;
    };
  }, [draft.materialId]);

  useEffect(() => {
    let cancelled = false;
    const userId = localStorage.getItem("userId");
    Promise.all([
      fetchList("api/color/explorer/exploration/objective", (row) => ({ id: row.id, name: row.name })),
      fetchList("api/color/explorer/laser/device", (row) => ({
        id: row.id,
        name: row.laser_device_name,
        parametersConfigName: row.parameters_config_name || "",
      })),
      fetchList(`api/color/explorer/material/?auth_user_id=${userId}`, (row) => ({
        id: row.id,
        name: row.material_name,
      })),
      fetchList("api/color/explorer/color/calibration/", (row) => ({
        id: row.id,
        name: row.name,
        illuminant_name: row.illuminant_name,
        camera_device: row.camera_device,
        illumination_device: row.illumination_device,
        function_order: row.function_order,
        mean_delta_e_00: row.mean_delta_e_00,
        max_delta_e_00: row.max_delta_e_00,
        loo_mean_delta_e_00: row.loo_mean_delta_e_00,
        loo_max_delta_e_00: row.loo_max_delta_e_00,
        created_at: row.created_at,
      })),
      fetchLatestExploration(),
    ]).then(([objectives, lasers, materials, calibrations, latest]) => {
      if (cancelled) return;
      const defaults = defaultsFromExploration(latest.row);
      setDefaultsScope(latest.row ? latest.scope : null);
      const objective = resolveChoice(defaults.objectiveId, objectives);
      const laser = resolveChoice(defaults.laserId, lasers);
      const material = resolveChoice(defaults.materialId, materials);
      const calibration = resolveChoice(defaults.cameraCalibrationId, calibrations);
      setLists({ objectives, lasers, materials, calibrations });
      setDraft((current) => ({
        ...current,
        objectiveId: objective?.id || "",
        objectiveName: objective?.name || "",
        laserId: laser?.id || "",
        laserName: laser?.name || "",
        laserConfigName: laser?.parametersConfigName || "",
        materialId: material?.id || "",
        materialName: material?.name || "",
        cameraCalibrationId: calibration?.id || "",
        plateOffset: defaults.plateOffset,
        markHeader: defaults.markHeader,
      }));
      // The tag is always new; a card whose value could not be pre-filled opens too.
      const open = new Set(["name"]);
      if (!objective) open.add("objective");
      if (!laser) open.add("laser");
      if (!material) open.add("substrate");
      if (!calibration) open.add("camera");
      setOpenCards(open);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const isOpen = (key) => openCards.has(key);
  const toggleCard = (key) => (next) =>
    setOpenCards((current) => {
      const copy = new Set(current);
      if (next) copy.add(key);
      else copy.delete(key);
      return copy;
    });
  // A review row opens its card and brings it into view.
  const jumpToCard = (key) => {
    toggleCard(key)(true);
    const header = document.getElementById(`setup-card-${key}-header`);
    if (header && typeof header.scrollIntoView === "function") {
      header.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const onOffsetChange = useCallback((plateOffset) => updateDraft({ plateOffset }), [updateDraft]);

  const tagValid = EXPLORATION_TAG_PATTERN.test(draft.tag);
  // The server stores the description with the exploration and refuses a blank one.
  const descriptionGiven = draft.description.trim().length > 0;
  const basisChosen = Boolean(draft.laserId && draft.materialId);
  const objectiveLoaded = Boolean(configs.explorationConfig?.optimization);
  const noObjective = objectiveLoaded && enabledObjectives(configs.explorationConfig).length === 0;
  const explorationName =
    tagValid && draft.materialName && draft.laserName
      ? composeExplorationName(draft.materialName, draft.laserName, draft.tag)
      : "";
  const nameSummary = tagValid
    ? previewExplorationName(draft.materialName, draft.laserName, draft.tag)
    : "Enter a tag";
  const calibrationRow = lists.calibrations.find((row) => row.id === draft.cameraCalibrationId) || null;
  const calibrationName = calibrationRow?.name || "";
  const positionMissing = basisChosen && !layout;
  const positionValue = `x ${draft.plateOffset.x}, y ${draft.plateOffset.y} mm • header ${draft.markHeader ? "on" : "off"}`;
  const positionSummary = !basisChosen
    ? "Choose a laser and a substrate first"
    : positionValue + (layout?.grid ? ` • ${layout.grid.rows} × ${layout.grid.cols} slots` : "");

  const startBlockers = useMemo(() => {
    const blockers = [];
    if (!tagValid) blockers.push("Exploration tag");
    if (!descriptionGiven) blockers.push("Description");
    if (!draft.objectiveId) blockers.push("Optimization preset");
    if (noObjective) blockers.push("At least one objective");
    if (invalid.optimization) blockers.push("Optimization settings");
    if (!draft.laserId) blockers.push("Laser system");
    if (invalid.laser) blockers.push("Laser settings");
    if (invalid.design) blockers.push("Design space");
    if (!draft.materialId) blockers.push("Substrate");
    if (invalid.substrate) blockers.push("Plate design");
    if (positionMissing) blockers.push("Substrate position");
    if (invalid.position) blockers.push("Header height");
    if (!draft.cameraCalibrationId) blockers.push("Camera calibration");
    if (invalid.width) blockers.push("Width measurement");
    return blockers;
  }, [tagValid, descriptionGiven, draft.objectiveId, noObjective, draft.laserId, draft.materialId, positionMissing, draft.cameraCalibrationId, invalid]);

  const populationSize = configs.explorationConfig?.optimization?.population_policy?.start_population_size;
  const plannedIterations = configs.explorationConfig?.expected_iterations;
  const objectiveCount = enabledObjectives(configs.explorationConfig).length;
  const ruleCount = enabledRules(configs.explorationConfig).length;
  const optimizationValue = draft.objectiveName
    ? [
        draft.objectiveName,
        populationSize && plannedIterations ? `${populationSize} points × ${plannedIterations} iterations` : "",
        objectiveLoaded ? `${objectiveCount} objective${objectiveCount === 1 ? "" : "s"}, ${ruleCount} rule${ruleCount === 1 ? "" : "s"}` : "",
        objectiveLoaded ? optimizerLabel(optimizerOf(configs.explorationConfig)) : "",
      ]
        .filter(Boolean)
        .join(" • ")
    : "Choose a preset";
  const laserValue = draft.laserName
    ? [draft.laserName, laserSummary(configs.laserConfig)].filter(Boolean).join(" • ")
    : "Choose a laser";
  const designValue = designSpaceSummary(configs.laserConfig, configs.explorationConfig);
  const designSummary = designValue || (basisChosen || draft.laserId ? "Loading" : "Choose a laser and a preset first");
  const substrateValue = draft.materialName
    ? [draft.materialName, plateDesignSummary(configs.plateDesignConfig)].filter(Boolean).join(" • ")
    : "Choose a substrate";
  const widthValue = widthSummary(configs.widthMeasurementConfig) || "Loading";

  // The header chip: where the defaults came from.
  const defaultsLabel = !ready
    ? "Loading the lists and your most recent exploration"
    : defaultsScope
    ? `Pre-filled from ${defaultsScope === "mine" ? "your" : "the team's"} most recent exploration`
    : "No earlier exploration to take defaults from: the first entry of each list is pre-selected";

  const cards = [
    {
      key: "name",
      step: 1,
      title: "Exploration name",
      description: "The tag you give this run; the ID is composed for you",
      summary: nameSummary,
      mono: tagValid,
      complete: tagValid && descriptionGiven,
      missing: ready && !(tagValid && descriptionGiven),
      review: {
        label: "Exploration tag",
        value: !tagValid ? "Enter a tag" : descriptionGiven ? draft.tag : "Add a description",
      },
      body: (
        <NameCard
          tag={draft.tag}
          description={draft.description}
          materialName={draft.materialName}
          laserName={draft.laserName}
          onTagChange={(tag) => updateDraft({ tag })}
          onDescriptionChange={(description) => updateDraft({ description })}
        />
      ),
    },
    {
      key: "objective",
      step: 2,
      title: "Optimization",
      description: "What the run optimises for, and how",
      summary: optimizationValue,
      complete: Boolean(draft.objectiveId) && !invalid.optimization && !noObjective,
      missing: ready && (!draft.objectiveId || Boolean(invalid.optimization) || noObjective),
      review: {
        label: "Optimization",
        value: invalid.optimization ? "Fix the settings" : noObjective ? "Switch on an objective" : optimizationValue,
      },
      body: (
        <OptimizationCard
          rows={lists.objectives}
          valueId={draft.objectiveId}
          onObjectiveChange={(row) => updateDraft({ objectiveId: row.id, objectiveName: row.name })}
          config={configs.explorationConfig}
          onConfigChange={configs.setExplorationConfig}
          onInvalidChange={invalidSetters.optimization}
          patchGrid={configs.plateDesignConfig?.patch_group_grid_size || null}
        />
      ),
    },
    {
      key: "laser",
      step: 3,
      title: "Laser system",
      description: "The laser the plates are marked with",
      summary: laserValue,
      complete: Boolean(draft.laserId) && !invalid.laser,
      missing: ready && (!draft.laserId || Boolean(invalid.laser)),
      review: { label: "Laser system", value: invalid.laser ? "Fix the settings" : laserValue },
      body: (
        <LaserCard
          rows={lists.lasers}
          valueId={draft.laserId}
          onLaserChange={(row) =>
            updateDraft({ laserId: row.id, laserName: row.name, laserConfigName: row.parametersConfigName || "" })
          }
          laserConfig={configs.laserConfig}
          onLaserConfigChange={configs.setLaserConfig}
          onInvalidChange={invalidSetters.laser}
        />
      ),
    },
    {
      key: "design",
      step: 4,
      title: "Design space",
      description: "The laser and cluster parameters the optimizer searches",
      summary: designSummary,
      complete: Boolean(designValue) && !invalid.design,
      missing: ready && Boolean(invalid.design),
      review: { label: "Design space", value: invalid.design ? "Fix the values" : designSummary },
      body: (
        <DesignSpaceCard
          laserConfig={configs.laserConfig}
          onLaserConfigChange={configs.setLaserConfig}
          explorationConfig={configs.explorationConfig}
          onExplorationConfigChange={configs.setExplorationConfig}
          onInvalidChange={invalidSetters.design}
        />
      ),
    },
    {
      key: "substrate",
      step: 5,
      title: "Substrate",
      description: "The material the patches are marked on, and the plate design",
      summary: substrateValue,
      complete: Boolean(draft.materialId) && !invalid.substrate,
      missing: ready && (!draft.materialId || Boolean(invalid.substrate)),
      review: { label: "Substrate", value: invalid.substrate ? "Fix the plate design" : substrateValue },
      body: (
        <SubstrateCard
          rows={lists.materials}
          valueId={draft.materialId}
          onMaterialChange={(row) => updateDraft({ materialId: row.id, materialName: row.name })}
          config={configs.plateDesignConfig}
          onConfigChange={configs.setPlateDesignConfig}
          onInvalidChange={invalidSetters.substrate}
          substrate={substrate}
        />
      ),
    },
    {
      key: "position",
      step: 6,
      title: "Substrate position",
      description: "Where the substrate sits under the laser",
      summary: positionSummary,
      complete: basisChosen && Boolean(layout) && !invalid.position,
      missing: ready && (positionMissing || Boolean(invalid.position)),
      review: {
        label: "Substrate position",
        value: invalid.position ? "Fix the header height" : basisChosen ? positionValue : "Choose a laser and a substrate first",
      },
      body: (
        <PlatePositionCard
          materialId={draft.materialId}
          laserDeviceId={draft.laserId}
          offset={draft.plateOffset}
          markHeader={draft.markHeader}
          plateDesignConfig={configs.plateDesignConfig}
          onPlateDesignChange={configs.setPlateDesignConfig}
          onOffsetChange={onOffsetChange}
          onHeaderChange={(markHeader) => updateDraft({ markHeader })}
          onLayoutChange={setLayout}
          onInvalidChange={invalidSetters.position}
        />
      ),
    },
    {
      key: "camera",
      step: 7,
      title: "Camera calibration",
      description: "How the photographed colours are converted",
      summary: calibrationSummary(calibrationRow) || "Choose a calibration",
      complete: Boolean(draft.cameraCalibrationId),
      missing: ready && !draft.cameraCalibrationId,
      review: { label: "Camera calibration", value: calibrationName || "Choose a calibration" },
      body: (
        <CalibrationCard
          rows={lists.calibrations}
          valueId={draft.cameraCalibrationId}
          onChange={(row) => updateDraft({ cameraCalibrationId: row.id })}
        />
      ),
    },
    {
      key: "width",
      step: 8,
      title: "Width measurement",
      description: "How the microscope photos are measured and classified",
      summary: widthValue,
      complete: Boolean(widthSummary(configs.widthMeasurementConfig)) && !invalid.width,
      missing: ready && Boolean(invalid.width),
      review: { label: "Width measurement", value: invalid.width ? "Fix the settings" : widthValue },
      body: (
        <WidthMeasurementCard
          config={configs.widthMeasurementConfig}
          onConfigChange={configs.setWidthMeasurementConfig}
          onInvalidChange={invalidSetters.width}
        />
      ),
    },
  ];
  const setupComplete = cards.filter((card) => card.complete).length;
  const reviewItems = cards.map((card) => ({
    key: card.key,
    label: card.review.label,
    value: ready ? card.review.value : "Loading",
    complete: card.complete,
    missing: card.missing,
  }));

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="New exploration"
        headerBadges={
          <Chip
            size="small"
            icon={<HistoryRoundedIcon />}
            label={defaultsLabel}
            data-testid="defaults-chip"
            sx={{ bgcolor: "#ffffff", fontWeight: 600, maxWidth: "100%", "& .MuiChip-label": { whiteSpace: "normal" }, height: "auto", py: 0.5 }}
          />
        }
        noPaper
      >
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 300px" },
            alignItems: "start",
            width: "100%",
          }}
        >
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            <GroupLabel label="Setup" hint={ready ? `${setupComplete} of ${cards.length} complete` : "Loading"} />
            {cards.map((card) => (
              <SetupCard
                key={card.key}
                cardKey={card.key}
                step={card.step}
                title={card.title}
                description={card.description}
                summary={card.summary}
                mono={card.mono}
                complete={card.complete}
                missing={card.missing}
                loading={!ready}
                expanded={isOpen(card.key)}
                onToggle={toggleCard(card.key)}
              >
                {card.body}
              </SetupCard>
            ))}
            <Box sx={{ pt: 1.5 }}>
              <GroupLabel label="Advanced" hint="Optional" />
            </Box>
            <ExplorationStartSection
              draft={draft}
              configs={configs}
              substrate={substrate}
              explorationName={explorationName}
              startBlockers={startBlockers}
              actionsContainer={actionsNode}
            />
          </Stack>
          <ReviewPanel
            explorationName={explorationName}
            items={reviewItems}
            onJump={jumpToCard}
            actionsRef={setActionsNode}
          />
        </Box>
      </ExplorationPageShell>
    </Stack>
  );
}
